import { useCallback, useRef, useState } from 'react';
import { useSpring } from 'react-spring';
import { useDrag } from 'react-use-gesture';

import { clamp } from '../helpers';

export default function useSlider({ slides }) {
  const [{ x, scale }, set] = useSpring(() => ({
    x: 0,
    scale: 1,
    config: { tension: 270, clamp: true },
  }));
  const index = useRef(0);

  // Slide numbers (for display purposes only)
  const [currentSlide, updateSlide] = useState(0);
  const [zooming, setZooming] = useState(false);

  const onScale = useCallback(
    (slideProps) => {
      set({ scale: slideProps.scale });
      const { translateX, translateY } = slideProps;

      if (slideProps.scale === 1 && !translateX && !translateY) {
        setZooming(false);
      } else {
        setZooming(true);
      }
    },
    [set],
  );

  const showNextImageX = (imageIndex) => {
    // Animate the transition
    set({
      x: -imageIndex * window.innerWidth,
    });

    // Update the slide number for display purposes
    updateSlide(imageIndex);
  };

  const bind = useDrag(
    ({ down, movement: [xMovement], direction: [xDir], distance, swipe: [swipeX], cancel, touches }) => {
      // We don't want to interrupt the pinch-to-zoom gesture
      if (touches > 1) {
        cancel();
      }

      // We have swiped past halfway
      if (!down && distance > window.innerWidth / 2) {
        // Move to the next slide
        const slideDir = xDir > 0 ? -1 : 1;

        index.current = clamp(index.current + slideDir, 0, slides.length - 1);

        set({
          x: -index.current * window.innerWidth + (down ? xMovement : 0),
          immediate: false,
        });
      } else if (swipeX !== 0) {
        // We have detected a swipe - update the new index
        index.current = clamp(index.current - swipeX, 0, slides.length - 1);
      }

      // Animate the transition
      set({
        x: -index.current * window.innerWidth + (down ? xMovement : 0),
        immediate: down,
      });

      // Update the slide number for display purposes
      updateSlide(index.current);
    },
    {
      axis: 'x',
      bounds: {
        left: currentSlide === slides.length - 1 ? 0 : -Infinity,
        right: index.current === 0 ? 0 : Infinity,
        top: 0,
        bottom: 0,
      },
      rubberband: true,
      enabled: slides.length > 1,
    },
  );

  return [zooming, scale, currentSlide, bind, x, onScale, showNextImageX];
}
